import { useState, useEffect } from 'react'

import { Loader } from '@mantine/core'

import { Metadata, useQuery } from '@redwoodjs/web'

import { GET_PUBLIC_COMPANY } from 'src/graphql/companies.graphql'
import { Logo, Name } from 'src/icons'

const PrivacyPolicyPage = ({ companyId }) => {
  const [logoError, setLogoError] = useState(false)

  const { data: companyData, loading: companyLoading } = useQuery(
    GET_PUBLIC_COMPANY,
    {
      skip: !companyId,
      variables: {
        companyId: companyId,
      },
    }
  )

  useEffect(() => {
    // If not a valid companyId, navigate to 404 page
    if (!companyData?.publicCompany && !companyLoading) {
      window.location.href = '/404'
    }
  }, [companyData, companyLoading])

  if (companyLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <Loader size="sm" color="gray" />
      </div>
    )
  }

  if (!companyData?.publicCompany) {
    return null
  }

  const companyName = companyData.publicCompany.companyName
  const privacyUrl = `${window.location.origin}/privacy/${companyId}`

  return (
    <>
      <Metadata
        title={`${companyName} SMS Privacy Policy`}
        description="SMS Privacy Policy"
      />
      <div className="flex min-h-screen flex-col bg-white">
        <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
          {/* Company Logo */}
          <div className="mb-8 flex justify-center">
            {logoError ? (
              <div className="flex flex-row gap-2">
                <img src={Logo} alt="Logo" className="h-12" />
                <img src={Name} alt="Name" className="h-12" />
              </div>
            ) : (
              <img
                src={`${process.env.AWS_PUBLIC_CDN_DOMAIN}/company/${companyId}/logo.png`}
                onError={() => {
                  setLogoError(true)
                }}
                alt={`${companyName} Logo`}
                className="h-12"
              />
            )}
          </div>

          {/* Privacy Policy Content */}
          <div className="prose prose-lg max-w-none">
            <h1 className="mb-6 text-3xl font-bold text-gray-900">
              {companyName} SMS Privacy Policy
            </h1>

            <p className="mb-6 text-lg text-gray-700">
              We have established the following policy for the use of our SMS
              service. Please read it carefully to understand how we collect,
              use, and manage your phone numbers.
            </p>

            <section className="mb-8">
              <h2 className="mb-4 text-2xl font-semibold text-gray-900">
                Collection of Information
              </h2>
              <p className="mb-4 text-gray-700">
                We collect your phone numbers and other related information such
                as your name, email address, and service preferences when you
                voluntarily provide them to us as part of the job application
                process.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="mb-4 text-2xl font-semibold text-gray-900">
                Use of Collected Information
              </h2>
              <p className="mb-4 text-gray-700">
                When you provide your mobile number and opt in to receive SMS
                messages from {companyName}, your consent and phone number are
                used solely for this messaging program. Mobile numbers and SMS
                consent are not shared, sold, rented, or transferred to third
                parties or affiliates for marketing or lead generation purposes.
                Service providers may be used only to deliver messages on our
                behalf and are not permitted to use the data for any other
                purpose.
              </p>
              <p className="mb-4 text-gray-700">
                Your phone numbers and related information are primarily used
                to:
              </p>
              <ul className="mb-4 list-disc pl-6 text-gray-700">
                <li className="mb-2">
                  Contact you to share more about our open job opportunities
                </li>
                <li className="mb-2">
                  Send SMS messages including updates on the status of your
                  application, request for additional information, interview
                  confirmations and additional relevant information.
                </li>
              </ul>
              <p className="mb-4 text-gray-700">
                We may share your phone numbers with:
              </p>
              <ul className="mb-4 list-disc pl-6 text-gray-700">
                <li className="mb-2">
                  Our SMS provider, subject to their privacy policy.
                </li>
                <li className="mb-2">
                  Trusted service providers who require the information to
                  deliver services on our behalf. All such parties are obligated
                  to keep your data secure and confidential.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="mb-4 text-2xl font-semibold text-gray-900">
                What to Expect
              </h2>
              <p className="mb-4 text-gray-700">
                By opting into this service, you can expect to receive
                notifications regarding scheduling and confirmation of interview
                dates, updates on the status of your job application, reminders
                for important tasks or appointments related to your application
                process, and links for completing required forms or
                documentation.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="mb-4 text-2xl font-semibold text-gray-900">
                Opting Out
              </h2>
              <p className="mb-4 text-gray-700">
                You can cancel the SMS service at any time. Just text{' '}
                <strong>STOP</strong> to the short code. After you send the SMS
                message <strong>STOP</strong> to us, we will send you an SMS
                message to confirm that you have been unsubscribed. After this,
                you will no longer receive SMS messages from us.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="mb-4 text-2xl font-semibold text-gray-900">
                Getting Help
              </h2>
              <p className="mb-4 text-gray-700">
                If you are experiencing issues with the messaging program, you
                can reply with the keyword <strong>HELP</strong> for more
                assistance.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="mb-4 text-2xl font-semibold text-gray-900">
                Carrier Liability
              </h2>
              <p className="mb-4 text-gray-700">
                Carriers are not liable for delayed or undelivered messages.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="mb-4 text-2xl font-semibold text-gray-900">
                Message and Data Rates
              </h2>
              <p className="mb-4 text-gray-700">
                As always, message and data rates may apply for any messages
                sent to you from us and to us from you. Message frequency
                varies. If you have any questions about your text plan or data
                plan, it is best to contact your wireless provider.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="mb-4 text-2xl font-semibold text-gray-900">
                Changes to our Privacy Notice
              </h2>
              <p className="mb-4 text-gray-700">
                We may change this Privacy Notice from time to time, and if we
                do, the most current version will be available at{' '}
                <a href={privacyUrl} className="text-blue-600 hover:underline">
                  {privacyUrl}
                </a>
                .
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  )
}

export default PrivacyPolicyPage
